import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { authApi, setAccessToken } from '../services/api';
import { setUserContext as setSentryUser, clearUserContext as clearSentryUser } from '../config/sentry';

// Events that reset the idle timer
const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

interface User {
  _id: string;
  email: string;
  role: string;
  profile: {
    firstName: string;
    lastName: string;
    avatar?: string;
    avatarColor?: string;
    phone?: string;
    timezone?: string;
    bio?: string;
    sessionIdleTimeout?: number; // minutes (15-240, default 30)
  };
  permissions?: string[];
}

interface Organization {
  _id: string;
  name: string;
  slug: string;
  subscription?: {
    tier: string;
    status: string;
    trialEndsAt?: string;
  };
  boldsign?: {
    status: string;
    contractsEnabled?: boolean;
  };
  contractSettings?: {
    notifyOnViewed?: boolean;
    notifyOnSigned?: boolean;
    notifyOnDeclined?: boolean;
    notifyOnExpired?: boolean;
    defaultExpirationDays?: number;
    defaultReminderDays?: number;
    autoRemindersEnabled?: boolean;
  };
  settings?: {
    timezone: string;
    currency: string;
    dateFormat: string;
    businessHours: {
      dayOfWeek: number;
      startTime: string;
      endTime: string;
      isEnabled: boolean;
    }[];
  };
}

interface DeviceAuthParams {
  fingerprint?: string;
  deviceToken?: string;
}

interface LoginResult {
  success: boolean;
  deviceToken?: string;
  requiresDeviceRegistration?: boolean;
  deviceStatus?: 'pending' | 'inactive' | 'revoked';
  userId?: string;
  organizationId?: string;
  message?: string;
}

interface AuthContextType {
  user: User | null;
  organization: Organization | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string, deviceAuth?: DeviceAuthParams) => Promise<LoginResult>;
  register: (data: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    organizationName: string;
    organizationSlug: string;
    serviceTypeId: string;
  }) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Refs for idle timeout management
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  // Force logout function - clears everything and redirects to login
  const forceLogout = useCallback(() => {
    queryClient.clear();
    localStorage.clear();
    sessionStorage.clear();
    setUser(null);
    setOrganization(null);
    setAccessToken(null);
    // Clear cookies by making logout request (ignore errors)
    authApi.logout().catch(() => {});
    window.location.href = '/login';
  }, [queryClient]);

  // Idle timeout logout with session expired message
  const idleLogout = useCallback(() => {
    queryClient.clear();
    localStorage.clear();
    sessionStorage.clear();
    setUser(null);
    setOrganization(null);
    setAccessToken(null);
    authApi.logout().catch(() => {});
    // Redirect with session expired flag
    window.location.href = '/login?session=expired';
  }, [queryClient]);

  // Reset the idle timer on user activity
  const resetIdleTimer = useCallback(() => {
    lastActivityRef.current = Date.now();

    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }

    // Only set timer if user is authenticated
    if (user) {
      const timeoutMs = (user.profile?.sessionIdleTimeout || 30) * 60 * 1000;
      idleTimerRef.current = setTimeout(() => {
        idleLogout();
      }, timeoutMs);
    }
  }, [user, idleLogout]);

  // Set up idle detection when user is authenticated
  useEffect(() => {
    if (!user) {
      // Clear timer when logged out
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }
      return;
    }

    // Set up event listeners for activity detection
    const handleActivity = () => resetIdleTimer();

    ACTIVITY_EVENTS.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Start the initial timer
    resetIdleTimer();

    // Cleanup
    return () => {
      ACTIVITY_EVENTS.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }
    };
  }, [user, resetIdleTimer]);

  // Check if user is authenticated on mount
  useEffect(() => {
    const initAuth = async () => {
      try {
        // Try to refresh token and get user data
        const refreshResponse = await authApi.refresh();
        setAccessToken(refreshResponse.data.tokens.accessToken);

        const meResponse = await authApi.me();
        setUser(meResponse.data.user);
        setOrganization(meResponse.data.organization);

        // Set Sentry user context on session restore
        setSentryUser({
          id: meResponse.data.user._id,
          email: meResponse.data.user.email,
          organizationId: meResponse.data.organization?._id,
          tier: meResponse.data.organization?.subscription?.tier,
        });
      } catch {
        // Not authenticated - clear any stale data
        setUser(null);
        setOrganization(null);
        setAccessToken(null);
        queryClient.clear();
        clearSentryUser();
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, [queryClient]);

  // Keyboard shortcut to force logout (Ctrl+Shift+L)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'L') {
        e.preventDefault();
        console.log('Force logout triggered');
        forceLogout();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [forceLogout]);

  const login = useCallback(async (email: string, password: string, deviceAuth?: DeviceAuthParams): Promise<LoginResult> => {
    // Clear any stale cached data from previous session
    queryClient.clear();

    try {
      const response = await authApi.login({
        email,
        password,
        fingerprint: deviceAuth?.fingerprint,
        deviceToken: deviceAuth?.deviceToken,
      });

      setAccessToken(response.data.tokens.accessToken);
      setUser(response.data.user);
      setOrganization(response.data.organization);

      // Set Sentry user context
      setSentryUser({
        id: response.data.user._id,
        email: response.data.user.email,
        organizationId: response.data.organization?._id,
        tier: response.data.organization?.subscription?.tier,
      });

      return {
        success: true,
        deviceToken: response.data.deviceToken,
      };
    } catch (error: unknown) {
      // Check if this is a device auth error
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { status?: number; data?: {
          requiresDeviceRegistration?: boolean;
          deviceStatus?: 'pending' | 'inactive' | 'revoked';
          userId?: string;
          organizationId?: string;
          error?: { message?: string };
        } } };

        if (axiosError.response?.status === 403) {
          const data = axiosError.response.data;

          if (data?.requiresDeviceRegistration) {
            return {
              success: false,
              requiresDeviceRegistration: true,
              userId: data.userId,
              organizationId: data.organizationId,
              message: data.error?.message || 'New device detected',
            };
          }

          if (data?.deviceStatus) {
            return {
              success: false,
              deviceStatus: data.deviceStatus,
              message: data.error?.message || 'Device not authorized',
            };
          }
        }
      }

      // Re-throw for other errors
      throw error;
    }
  }, [queryClient]);

  const register = useCallback(async (data: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    organizationName: string;
    organizationSlug: string;
    serviceTypeId: string;
  }) => {
    const response = await authApi.register(data);
    setAccessToken(response.data.tokens.accessToken);
    setUser(response.data.user);
    setOrganization(response.data.organization);

    // Set Sentry user context
    setSentryUser({
      id: response.data.user._id,
      email: response.data.user.email,
      organizationId: response.data.organization?._id,
      tier: response.data.organization?.subscription?.tier,
    });
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } finally {
      // CRITICAL: Clear all cached data to prevent data leakage between users
      queryClient.clear();

      // Clear local/session storage
      localStorage.clear();
      sessionStorage.clear();

      // Reset auth state
      setUser(null);
      setOrganization(null);
      setAccessToken(null);

      // Clear Sentry user context
      clearSentryUser();
    }
  }, [queryClient]);

  const refreshUser = useCallback(async () => {
    const response = await authApi.me();
    setUser(response.data.user);
    setOrganization(response.data.organization);
  }, []);

  const value: AuthContextType = {
    user,
    organization,
    isLoading,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
